import type { TenantCustomization } from './types';
import { keilogCustomization } from './keilog/manifest';

/**
 * Tenant customization registry.
 *
 * Adding a site type is: create `customize/<code>/`, export a
 * `TenantCustomization`, add one line here. No file under `src/` changes.
 */
const registry: Record<string, TenantCustomization> = {
  [keilogCustomization.code]: keilogCustomization,
};

/** Customization for a site type, or `undefined` when it has none. */
export function getTenantCustomization(
  siteType?: string | null
): TenantCustomization | undefined {
  if (!siteType) return undefined;
  const found = registry[siteType.toLowerCase()];
  return found?.enabled ? found : undefined;
}

/** Every enabled customization. Used to build the core registries at startup. */
export function getTenantCustomizations(): TenantCustomization[] {
  return Object.values(registry).filter(t => t.enabled);
}

/** Resolve one generic core policy without naming a tenant in `src/`. */
export function tenantCapability<K extends keyof NonNullable<TenantCustomization['capabilities']>>(
  siteType: string | null | undefined,
  capability: K,
  fallback: boolean
): boolean {
  return getTenantCustomization(siteType)?.capabilities?.[capability] ?? fallback;
}

/** Site types that ship a customization bundle. */
export function getCustomizedSiteTypes(): string[] {
  return getTenantCustomizations().map(t => t.code);
}

export type { TenantCustomization };
